import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Grid, 
  Button, 
  Container,
  TablePagination,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  styled,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Menu,
  DialogContentText,
  Snackbar,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReportsHubBack } from '../../reports/hub/useReportsHubBack';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonIcon from '@mui/icons-material/Person';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import FilterListIcon from '@mui/icons-material/FilterList';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getSalesScreenOptions } from '../../../services/migration/salesScreenDataService';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoIcon from '@mui/icons-material/Info';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import trLocale from 'date-fns/locale/tr';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DescriptionIcon from '@mui/icons-material/Description';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { format, isAfter, isBefore, isValid, startOfDay, endOfDay, parse } from 'date-fns';
import TransactionLogsModal from './TransactionLogsModal';
import InvoiceDisplayModal from '../invoice/InvoiceDisplayModal';
import AccountingDataTable, { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';
import enLocale from 'date-fns/locale/en-US';
import { useAuth } from '../../../contexts/AuthContext';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Filtreler için styled component
const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex', 
  gap: 1, 
  flexWrap: 'wrap',
  marginBottom: theme.spacing(2)
}));

// İşlem türleri için sabit tanımlamalar
const TRANSACTION_TYPES = {
  SALE_WITH_STOCK: 'SALE_WITH_STOCK',
  SALE_WITHOUT_STOCK: 'SALE_WITHOUT_STOCK',
  COLLECTION: 'COLLECTION',
  PAYMENT: 'PAYMENT',
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
  INVOICE: 'invoice',
  OFFSETTING: 'offsetting',
  QUICK_SALE: 'QUICK_SALE',
  SALES_INVOICE: 'SALES_INVOICE',
  PURCHASE_INVOICE: 'PURCHASE_INVOICE'
};

// İşlem türlerinin görünen isimleri
const TRANSACTION_TYPE_NAMES = {
  [TRANSACTION_TYPES.SALE_WITH_STOCK]: 'Stoklu Satış',
  [TRANSACTION_TYPES.SALE_WITHOUT_STOCK]: 'Stoksuz Satış',
  [TRANSACTION_TYPES.COLLECTION]: 'Tahsilat',
  [TRANSACTION_TYPES.PAYMENT]: 'Ödeme',
  [TRANSACTION_TYPES.CREDIT]: 'Alacaklandırma',
  [TRANSACTION_TYPES.DEBIT]: 'Borçlandırma',
  [TRANSACTION_TYPES.INVOICE]: 'Fatura',
  [TRANSACTION_TYPES.OFFSETTING]: 'Mahsup',
  [TRANSACTION_TYPES.QUICK_SALE]: 'Hızlı Satış',
  [TRANSACTION_TYPES.SALES_INVOICE]: 'Satış Faturası',
  [TRANSACTION_TYPES.PURCHASE_INVOICE]: 'Alış Faturası'
};

const TRANSACTION_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  [TRANSACTION_TYPES.SALE_WITH_STOCK]: 'current.report.transactionTypes.saleWithStock',
  [TRANSACTION_TYPES.SALE_WITHOUT_STOCK]: 'current.report.transactionTypes.saleWithoutStock',
  [TRANSACTION_TYPES.COLLECTION]: 'current.report.transactionTypes.collection',
  [TRANSACTION_TYPES.PAYMENT]: 'current.report.transactionTypes.payment',
  [TRANSACTION_TYPES.CREDIT]: 'current.report.transactionTypes.credit',
  [TRANSACTION_TYPES.DEBIT]: 'current.report.transactionTypes.debit',
  [TRANSACTION_TYPES.INVOICE]: 'current.report.transactionTypes.invoice',
  [TRANSACTION_TYPES.OFFSETTING]: 'current.report.transactionTypes.offsetting',
  [TRANSACTION_TYPES.QUICK_SALE]: 'current.report.transactionTypes.quickSale',
  [TRANSACTION_TYPES.SALES_INVOICE]: 'current.report.transactionTypes.salesInvoice',
  [TRANSACTION_TYPES.PURCHASE_INVOICE]: 'current.report.transactionTypes.purchaseInvoice'
};

// Kullanılacak ikonlar
const TRANSACTION_TYPE_ICONS: { [key: string]: React.ReactNode } = {
  [TRANSACTION_TYPES.PAYMENT]: <PaymentIcon />,
  [TRANSACTION_TYPES.COLLECTION]: <ReceiptIcon />,
  [TRANSACTION_TYPES.INVOICE]: <DescriptionIcon />,
  [TRANSACTION_TYPES.OFFSETTING]: <CompareArrowsIcon />,
  [TRANSACTION_TYPES.QUICK_SALE]: <PointOfSaleIcon />,
  [TRANSACTION_TYPES.CREDIT]: <ArrowDownwardIcon color="success" />,
  [TRANSACTION_TYPES.DEBIT]: <ArrowUpwardIcon color="error" />,
  [TRANSACTION_TYPES.SALES_INVOICE]: <ShoppingBagIcon color="primary" />,
  [TRANSACTION_TYPES.PURCHASE_INVOICE]: <ShoppingCartIcon color="secondary" />,
};

// İşlem arayüzü
interface Transaction {
  id: string;
  accountId: string;
  accountName?: string;
  type: string;
  transactionType?: string; // Yeni işlem türü alanı
  operationType?: string; // İşlem operasyon türü (sales, purchase, vs.)
  amount: number;
  debitAmount?: number; // Borç miktarı
  creditAmount?: number; // Alacak miktarı
  debitBalance?: number; // İşlem sonrası borç bakiyesi
  description: string;
  documentNo?: string;
  referenceId?: string; // Fatura veya belge ID'si
  referenceType?: string; // Referansın türü (salesinvoice, purchaseinvoice, vs)
  invoiceNumber?: string; // Fatura numarası
  notes?: string; // Fatura notları
  date?: string; // İşlem tarihi
  createdAt: string;
  createdBy?: string;
  isIncome: boolean; // Gelir mi gider mi?
  currency?: string; // Para birimi
  exchangeRate?: number; // Döviz kuru
  previousBalance: number;
  nextBalance: number;
  screenId?: string | null; // İşlemin yapıldığı satış ekranı ID'si
  screenName?: string | null; // İşlemin yapıldığı satış ekranı adı
}

// Cari hesap arayüzü
interface Account {
  id: string;
  companyName: string;
}

// Tablo sütunu arayüzü
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  description?: string;
}

interface AccountMovementsReportProps {
  /** Manager paneli: şube filtresi bu şubeye kilitlenir */
  lockedBranchId?: string | null;
  /** Embedded modda geri butonu bu callback'i çağırır (route'a dönmek yerine) */
  onBack?: () => void;
}

const AccountMovementsReport: React.FC<AccountMovementsReportProps> = ({
  lockedBranchId = null,
  onBack
}) => {
  const { t, i18n } = useTranslation('accounting');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [totalCount, setTotalCount] = useState(0); // ✅ Backend'den gelen toplam kayıt sayısı
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [selectedTransactionType, setSelectedTransactionType] = useState<string | null>(null);
  const [isTableSettingsModalOpen, setIsTableSettingsModalOpen] = useState(false);
  const [showTableSettingsInfoBox, setShowTableSettingsInfoBox] = useState(false);
  const [isReportInfoModalOpen, setIsReportInfoModalOpen] = useState(false);
  const [topActionsAnchorEl, setTopActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Tarih aralığı filtreleme için state'ler
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDateInputValue, setStartDateInputValue] = useState<string>('');
  const [endDateInputValue, setEndDateInputValue] = useState<string>('');
  
  // Satış ekranları için yeni state'ler ekleyelim
  const [salesScreens, setSalesScreens] = useState<{ id: string; name: string; type: string }[]>([]);
  const [selectedSalesScreens, setSelectedSalesScreens] = useState<string[]>([]);
  
  // ✅ Şube filtresi (eski kayıtlarda branchId yok = Genel; manager panelinde kilitli)
  const { branches } = useAuth();
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<string>(lockedBranchId || '');
  
  // İşlem menüsü için state'ler
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  
  // Silme onay modalı için state'ler
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  
  // Başarı snackbar için state'ler
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Hata snackbar için state'ler
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Loglar modalı için state
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  
  // Fatura görüntüleme modalı için state'ler
  const [isInvoiceDisplayModalOpen, setIsInvoiceDisplayModalOpen] = useState(false);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState<any>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const getTransactionTypeLabel = (type?: string) => {
    const translationKey = type ? TRANSACTION_TYPE_TRANSLATION_KEYS[type] : undefined;
    if (!translationKey) {
      return t('current.report.transactionTypes.other');
    }

    return t(translationKey);
  };
  
  // Tablo sütunları
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'date', label: t('current.report.columns.date'), field: 'createdAt', visible: true, fixed: true, description: t('current.report.columnsDesc.date') },
    { id: 'transactionType', label: t('current.report.columns.transactionType'), field: 'transactionType', visible: true, fixed: true, description: t('current.report.columnsDesc.transactionType') },
    { id: 'account', label: t('current.report.columns.account'), field: 'accountName', visible: true, fixed: true, description: t('current.report.columnsDesc.account') },
    { id: 'previousBalance', label: t('current.report.columns.previousBalance'), field: 'previousBalance', visible: false, fixed: false, description: t('current.report.columnsDesc.previousBalance') },
    { id: 'amount', label: t('current.report.columns.amount'), field: 'amount', visible: true, fixed: true, description: t('current.report.columnsDesc.amount') },
    { id: 'debitAmount', label: t('current.report.columns.debitAmount'), field: 'debitAmount', visible: true, fixed: false, description: t('current.report.columnsDesc.debitAmount') },
    { id: 'creditAmount', label: t('current.report.columns.creditAmount'), field: 'creditAmount', visible: true, fixed: false, description: t('current.report.columnsDesc.creditAmount') },
    { id: 'nextBalance', label: t('current.report.columns.nextBalance'), field: 'nextBalance', visible: false, fixed: false, description: t('current.report.columnsDesc.nextBalance') },
    { id: 'currency', label: t('current.report.columns.currency'), field: 'currency', visible: true, fixed: false, description: t('current.report.columnsDesc.currency') },
    { id: 'screenName', label: t('current.report.columns.screenName'), field: 'screenName', visible: false, fixed: false, description: t('current.report.columnsDesc.screenName') },
    { id: 'actions', label: t('current.report.columns.actions'), field: 'actions', visible: true, fixed: true, description: t('current.report.columnsDesc.actions') },
  ]);
  
  // LocalStorage key
  const ACCOUNT_MOVEMENTS_TABLE_SETTINGS_KEY = 'accountMovementsTableSettings';
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // LocalStorage'dan tablo ayarlarını yükle
  useEffect(() => {
    const savedSettings = localStorage.getItem(ACCOUNT_MOVEMENTS_TABLE_SETTINGS_KEY);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        
        // Yeni eklenmiş sütun varsa, varsayılan ayarlarını koruyarak ekle
        const updatedColumns = tableColumns.map(defaultColumn => {
          const savedColumn = parsedSettings.find((col: TableColumn) => col.id === defaultColumn.id);
          if (savedColumn) {
            return {
              ...defaultColumn,
              visible: defaultColumn.fixed ? true : savedColumn.visible
            };
          }
          return defaultColumn;
        });
        
        setTableColumns(updatedColumns);
      } catch (e) {
        console.error('Kayıtlı tablo ayarları yüklenirken hata:', e);
      }
    }
  }, []);

  // Tablo ayarlarını kaydet
  const saveTableSettings = (columns: TableColumn[]) => {
    setTableColumns(columns);
    localStorage.setItem(ACCOUNT_MOVEMENTS_TABLE_SETTINGS_KEY, JSON.stringify(columns));
  };

  // Verileri yükleme
  useEffect(() => {
    loadCompanyAndData();
  }, []);

  // Şimdi SQL'den işlemleri yükleyelim - sayfa veya filtre değişince yeniden yükle
  useEffect(() => {
    if (companyId) {
      loadAccountTransactions(companyId);
    }
  }, [companyId, page, rowsPerPage, accountSearchTerm, selectedTransactionType, startDate, endDate, sortColumn, sortDirection, selectedBranchFilter]);

  const loadCompanyAndData = async () => {
    try {
      setLoading(true);
      
      // Kullanıcı oturum açmış mı kontrol et
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setLoading(false);
        setError(t('current.report.errors.userNotLoggedIn'));
        return;
      }
      
      // Kullanıcının şirket ID'sini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      
      if (!userData) {
        setLoading(false);
        setError(t('current.report.errors.userNotFound'));
        return;
      }
      
      const companyId = userData.companyId;
      setCompanyId(companyId || null);
      
      if (!companyId) {
        setLoading(false);
        setError(t('current.report.errors.companyNotFound'));
        return;
      }
      
      // Satış ekranlarını yükle
      loadSalesScreens(companyId);
      
      // Cari hesapları yükle
      loadAccounts(companyId);
      
      // Not: loadAccountTransactions'ı useEffect içinde çağıracağız
      
    } catch (error) {
      console.error('Şirket veya veri yüklenirken hata:', error);
      setLoading(false);
      setError(t('current.report.errors.dataLoad'));
    }
  };

  // Satış ekranlarını yükle (SQL — Hızlı Satış + PYK)
  const loadSalesScreens = async (companyId: string) => {
    try {
      const screensArray = await getSalesScreenOptions(companyId);
      setSalesScreens(screensArray);
    } catch (error) {
      console.error('Satış ekranları yüklenirken hata:', error);
    }
  };

  // Cari hesapları yükleme - SQL'den
  const loadAccounts = async (companyId: string) => {
    try {
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);

      const result = response.data;
      const sqlAccounts = result.data || result;
      
      const accountsList: Account[] = sqlAccounts
        .filter((data: any) => data.isActive !== false)
        .map((data: any) => ({
          id: data.id.toString(),
          companyName: data.companyName || '',
        }));
      
      // Hesapları isme göre sırala
      accountsList.sort((a, b) => a.companyName.localeCompare(b.companyName));
      
      setAccounts(accountsList);
    } catch (error) {
      console.error('Cari hesaplar yüklenirken hata:', error);
      setError(handleApiError(error));
    }
  };

  // ✅ SAYFALANMIŞ cari işlemleri SQL'den yükleme (Performans için)
  const loadAccountTransactions = async (companyId: string) => {
    try {
      setLoading(true);
      
      // ✅ Sayfalanmış endpoint kullan (tüm kayıtları çekme!)
      const url = `${ACCOUNTING_API.TRANSACTIONS_BY_COMPANY}/paged?page=${page + 1}&pageSize=${rowsPerPage}${
        accountSearchTerm ? `&searchTerm=${encodeURIComponent(accountSearchTerm)}` : ''
      }${
        selectedTransactionType ? `&transactionType=${encodeURIComponent(selectedTransactionType)}` : ''
      }${
        startDate ? `&startDate=${startDate.toISOString()}` : ''
      }${
        endDate ? `&endDate=${endDate.toISOString()}` : ''
      }${
        sortColumn ? `&sortColumn=${encodeURIComponent(sortColumn)}` : ''
      }${
        sortDirection ? `&sortDirection=${encodeURIComponent(sortDirection)}` : ''
      }${
        selectedBranchFilter ? `&branchId=${encodeURIComponent(selectedBranchFilter)}` : ''
      }`;
      
      const response = await accountingApi.get(url);

      const result = response.data;
      const sqlTransactions = result.data || [];
      const totalCount = result.pagination?.totalCount || 0;
      
      console.log(`📊 [PAGINATION] Sayfa ${page + 1}, Toplam ${totalCount} kayıt, ${sqlTransactions.length} kayıt yüklendi`);
      
      // 🔍 DEBUG: Backend'den gelen HAM veriyi logla (ilk 5 kayıt)
      console.log('🔍 [DEBUG - BACKEND HAM VERİ] İlk 5 kayıt (backend sıralaması):', 
        sqlTransactions.slice(0, 5).map((t: any, index: number) => ({
          sıra: index + 1,
          id: t.id,
          date: t.date,
          createdAt: t.createdAt,
          description: t.description?.substring(0, 30),
          accountName: t.accountName
        }))
      );
      
      // SQL'den gelen verileri frontend formatına çevir
      const transactionsList: Transaction[] = sqlTransactions.map((data: any) => {
        // Ekran adını bul (şimdilik Firebase'den alınmıyor, ileride eklenebilir)
        let screenName: string | null = null;
        if (data.screenId) {
          const screen = salesScreens.find(s => s.id === data.screenId);
          screenName = screen ? screen.name : 'Bilinmeyen Ekran';
        }
        
        // Account name'i SQL'den al veya accounts listesinden bul
        let accountName = data.accountName || '';
        if (!accountName && data.accountId) {
          const account = accounts.find(acc => acc.id === data.accountId.toString());
          if (account) {
            accountName = account.companyName;
          }
        }
        
        return {
          id: data.id.toString(),
          accountId: data.accountId?.toString() || '',
          accountName: accountName,
          type: data.transactionType || '',
          transactionType: data.transactionType || '',
          operationType: data.operationType || '',
          amount: parseFloat(data.amount) || 0,
          debitAmount: parseFloat(data.debitAmount) || 0,
          creditAmount: parseFloat(data.creditAmount) || 0,
          debitBalance: parseFloat(data.debitBalance) || 0,
          description: data.description || '',
          documentNo: data.documentNo || '',
          referenceId: data.referenceId || '',
          referenceType: data.referenceType || '',
          invoiceNumber: data.invoiceNumber || '',
          notes: data.notes || '',
          date: data.date || '',
          createdAt: data.createdAt || '',
          createdBy: data.createdBy || '',
          isIncome: data.creditAmount > 0, // creditAmount varsa gelir, yoksa gider
          currency: data.currency || 'TRY',
          exchangeRate: parseFloat(data.exchangeRate) || 1,
          previousBalance: 0, // Başlangıçta 0, sonra hesaplanacak
          nextBalance: 0,     // Başlangıçta 0, sonra hesaplanacak
          screenId: data.screenId || null,
          screenName
        };
      });
      
      // 🔍 DEBUG: Backend'den gelen ilk 5 kayıt (RAW)
      console.log('🔍 [DEBUG] Backend\'den gelen ilk 5 kayıt (ham):', transactionsList.slice(0, 5).map(t => ({
        id: t.id,
        createdAt: t.createdAt,
        date: t.date,
        description: t.description?.substring(0, 30)
      })));
      
      // ✅ Backend'in (Date ?? CreatedAt) bazlı sıralamasını kullanıyoruz
      // Frontend'de SIRALAMA YAPMIYORUZ! Backend zaten doğru sıralamayla gönderdi.
      console.log('✅ [SIRALAMA] Backend\'den gelen ilk 5 işlem (Date ?? CreatedAt bazlı):', transactionsList.slice(0, 5).map(t => ({
        id: t.id,
        date: t.date ? formatDate(t.date) : '-',
        createdAt: formatDate(t.createdAt),
        sortDate: t.date ? formatDate(t.date) : formatDate(t.createdAt),
        description: t.description?.substring(0, 30)
      })));
      
      // Hesap bazında bakiyeleri hesapla
      calculateBalances(transactionsList);
      
      setTransactions(transactionsList);
      setFilteredTransactions(transactionsList);
      setTotalCount(totalCount);
      setLoading(false);

      // 🔍 DEBUG: Frontend'de son durum
      console.log('✅ [DEBUG - FRONTEND SONUÇ] İlk 5 işlem:', 
        transactionsList.slice(0, 5).map((t, index) => ({
          sıra: index + 1,
          id: t.id,
          date: t.date ? formatDate(t.date) : '-',
          createdAt: formatDate(t.createdAt),
          sortDate: t.date ? formatDate(t.date) : formatDate(t.createdAt),
          description: t.description?.substring(0, 30),
          accountName: t.accountName
        }))
      );
      
    } catch (error) {
      console.error('SQL cari hareketler yükleme hatası:', error);
      setLoading(false);
      setError(handleApiError(error));
    }
  };

  // Hesap bazında bakiyeleri hesapla
  const calculateBalances = (transactionsList: Transaction[]) => {
    // Her hesap için bakiye takibi yapacağız
    const accountBalances: Record<string, number> = {};

    // İşlemleri tarih sırasına göre sırala (eskiden yeniye)
    const sortedTransactions = [...transactionsList].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Her işlem için sırayla bakiye hesapla
    sortedTransactions.forEach(transaction => {
      if (!transaction.accountId) return;
      
      // Hesap için başlangıç bakiyesi (daha önce işlem yapılmışsa)
      const currentBalance = accountBalances[transaction.accountId] || 0;
      
      // Önceki bakiyeyi ayarla
      transaction.previousBalance = currentBalance;
      
      // İşlem türüne göre bakiyeyi güncelle
      // Alacak (+), Borç (-)
      const debitAmount = transaction.debitAmount || 0;
      const creditAmount = transaction.creditAmount || 0;
      
      // Bakiye hesaplama: Alacak - Borç
      const balanceChange = creditAmount - debitAmount;
      
      // Yeni bakiye
      const newBalance = currentBalance + balanceChange;
      
      // Sonraki bakiyeyi ayarla
      transaction.nextBalance = newBalance;
      
      // Hesabın bakiyesini güncelle
      accountBalances[transaction.accountId] = newBalance;
    });
    
    // Şimdi tarih sırasını tersine çevir (yeniden eskiye)
    transactionsList.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  };

  // ✅ Arama ve filtreleme artık BACKEND'de yapılıyor (SQL seviyesinde)
  // Frontend'de sadece satış ekranı filtresi ve genel arama terimi kaldı (backend'de yok)
  useEffect(() => {
    let results = [...transactions];
    
    // Sadece satış ekranı filtresi frontend'de (backend endpoint'inde bu parametre yok)
    if (selectedSalesScreens.length > 0) {
      results = results.filter(transaction => 
        transaction.screenId && selectedSalesScreens.includes(transaction.screenId)
      );
    }
    
    // Genel arama terimi frontend'de (description ve documentNo arama)
    if (searchTerm.trim() !== '') {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      results = results.filter(transaction => {
        return (transaction.description && transaction.description.toLowerCase().includes(lowerCaseSearchTerm)) ||
               (transaction.documentNo && transaction.documentNo.toLowerCase().includes(lowerCaseSearchTerm));
      });
    }
    
    setFilteredTransactions(results);
  }, [searchTerm, selectedSalesScreens, transactions]);

  // ✅ Backend zaten sayfalıyor, frontend'de tekrar sayfalamaya gerek yok
  // Direkt filteredTransactions kullan (frontend'de sadece satış ekranı ve genel arama filtresi var)
  const paginatedTransactions = filteredTransactions;

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Server-side sıralama
  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortColumn(field);
    setSortDirection(direction);
    setPage(0);
  };

  // Geri butonu
  const { goBack: handleGoBack } = useReportsHubBack('/cari-hareket-raporu', () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate('/cari');
  });

  // İşlem tipine göre ikon ve renk
  const getTransactionTypeIcon = (transaction: Transaction) => {
    // transactionType alanı varsa onu kullan
    const type = transaction.transactionType || transaction.type;
    const isIncome = transaction.isIncome;
    
    switch (type) {
      case TRANSACTION_TYPES.INVOICE:
        if (transaction.operationType === 'sales') {
          return <ReceiptIcon fontSize="small" color="primary" />;
        } else if (transaction.operationType === 'purchase') {
          return <ReceiptIcon fontSize="small" color="secondary" />;
        }
        return <ReceiptIcon fontSize="small" />;
      case TRANSACTION_TYPES.SALE_WITH_STOCK:
      case TRANSACTION_TYPES.SALE_WITHOUT_STOCK:
        return <ShoppingBagIcon fontSize="small" color="primary" />;
      case TRANSACTION_TYPES.COLLECTION:
        return <ArrowDownwardIcon fontSize="small" color="success" />;
      case TRANSACTION_TYPES.PAYMENT:
        return <ArrowUpwardIcon fontSize="small" color="error" />;
      case TRANSACTION_TYPES.CREDIT:
        return <ArrowDownwardIcon fontSize="small" color="success" />;
      case TRANSACTION_TYPES.DEBIT:
        return <ArrowUpwardIcon fontSize="small" color="error" />;
      default:
        return isIncome 
          ? <ArrowDownwardIcon fontSize="small" color="success" /> 
          : <ArrowUpwardIcon fontSize="small" color="error" />;
    }
  };

  // İşlemin türünü görüntüle
  const renderTransactionType = (transaction: Transaction) => {
    // transactionType alanı varsa onu kullan
    const type = transaction.transactionType || transaction.type;
    
    // Özel işlem türü isimleri
    let typeName = getTransactionTypeLabel(type);
    
    // Fatura işlemi türleri
    if (type === TRANSACTION_TYPES.INVOICE) {
      if (transaction.operationType === 'sales') {
        typeName = 'Satış Faturası';
      } else if (transaction.operationType === 'purchase') {
        typeName = 'Alış Faturası';
      }
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getTransactionTypeIcon(transaction)}
        <Typography variant="body2">
          {typeName}
        </Typography>
      </Box>
    );
  };

  // Tarihi formatla (Yerel saat - Backend'den zaten Türkiye saatinde geliyor)
  const formatDate = (dateString: string, transaction?: any) => {
    if (!dateString) return '-';
    
    try {
      // Date nesnesine çevir
      let date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        // Geçersiz tarih formatı
        return dateString;
      }
      
      // ✅ YENİ: Backend'den zaten yerel saat (Türkiye) geliyor, +3 eklemeye gerek yok
      // Manuel formatla (yerel saat)
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return dateString; // Hata durumunda orijinal dizeyi döndür
    }
  };

  // Para formatı
  const formatCurrency = (amount: number, currencyCode: string = 'TRY'): string => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: currencyCode 
    }).format(amount);
  };

  // Hareket tipine göre renk
  const getTransactionTypeChipColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case TRANSACTION_TYPES.INVOICE:
        return 'primary';
      case TRANSACTION_TYPES.SALE_WITH_STOCK:
      case TRANSACTION_TYPES.SALE_WITHOUT_STOCK:
        return 'primary';
      case TRANSACTION_TYPES.COLLECTION:
        return 'success';
      case TRANSACTION_TYPES.PAYMENT:
        return 'error';
      case TRANSACTION_TYPES.CREDIT:
        return 'success';
      case TRANSACTION_TYPES.DEBIT:
        return 'error';
      default:
        return 'default';
    }
  };

  // Tablo ayarları modalını açma
  const handleOpenTableSettingsModal = () => {
    setIsTableSettingsModalOpen(true);
  };

  // Tablo ayarları modalını kapatma
  const handleCloseTableSettingsModal = () => {
    setIsTableSettingsModalOpen(false);
  };

  // Tablo sütun ayarlarını güncelle
  const handleUpdateTableSettings = (columns: TableColumn[]) => {
    saveTableSettings(columns);
  };

  // Aktif sütunların listesini oluştur
  const visibleColumns = tableColumns.filter(column => column.visible);

  // Hareket tipini temizle ve sayfa sıfırla
  const handleClearTransactionTypeFilter = () => {
    setSelectedTransactionType(null);
    setPage(0);
  };

  // Cari hesap seçimini temizle ve sayfa sıfırla
  const handleClearAccountFilter = () => {
    setAccountSearchTerm('');
    setPage(0);
  };

  // Tarih filtrelerini temizle ve sayfa sıfırla
  const handleClearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStartDateInputValue('');
    setEndDateInputValue('');
    setPage(0);
  };

  // Tüm filtreleri temizle ve sayfa sıfırla
  const handleClearAllFilters = () => {
    setSearchTerm('');
    setAccountSearchTerm('');
    setSelectedTransactionType(null);
    setSelectedSalesScreens([]);
    handleClearDateFilters();
    setPage(0);
  };

  // Satış ekranı filtresini temizleme fonksiyonu ekleyelim
  const handleClearSalesScreenFilter = () => {
    setSelectedSalesScreens([]);
    setPage(0);
  };

  // Log modalı açma/kapatma
  const handleOpenLogsModal = () => {
    setIsLogsModalOpen(true);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
  };

  const handleOpenTopActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setTopActionsAnchorEl(event.currentTarget);
  };

  const handleCloseTopActionsMenu = () => {
    setTopActionsAnchorEl(null);
  };

  const handleOpenReportInfoModal = () => {
    setIsReportInfoModalOpen(true);
    handleCloseTopActionsMenu();
  };

  const handleCloseReportInfoModal = () => {
    setIsReportInfoModalOpen(false);
  };

  // Menü işlemleri
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDeleteTransaction = () => {
    if (!selectedTransaction) return;
    
    setTransactionToDelete(selectedTransaction);
    setIsDeleteConfirmModalOpen(true);
    handleCloseMenu();
  };

  // ✅ YENİ: Faturayı görüntüle (Modal aç)
  const handleViewInvoice = async () => {
    if (!selectedTransaction?.referenceId) {
      setErrorMessage(t('current.report.errors.invoiceNotFound'));
      setErrorSnackbarOpen(true);
      handleCloseMenu();
      return;
    }

    try {
      setLoadingInvoice(true);
      handleCloseMenu();

      console.log('📄 [FATURA GÖRÜNTÜLE] Fatura yükleniyor...', { 
        referenceId: selectedTransaction.referenceId,
        invoiceNumber: selectedTransaction.invoiceNumber,
        companyId: companyId
      });

      // ✅ MULTI-TENANT: companyId parametresi ekle
      if (!companyId) {
        throw new Error(t('current.report.errors.companyIdNotFound'));
      }

      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(selectedTransaction.referenceId));

      const result = response.data;
      const invoiceData = result.data || result;

      console.log('✅ [FATURA GÖRÜNTÜLE] Fatura yüklendi:', invoiceData);

      // InvoiceDisplayModal için veri formatını düzenle
      const formattedInvoiceData = {
        id: invoiceData.id,
        invoiceNumber: invoiceData.invoiceNumber,
        type: invoiceData.invoiceType?.toLowerCase() === 'sales' ? 'sales' : 
              invoiceData.invoiceType?.toLowerCase() === 'purchase' ? 'purchase' : 'quick_sale',
        date: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate,
        account: {
          companyName: invoiceData.accountName || selectedTransaction.accountName || '-',
          taxNumber: invoiceData.accountTaxNumber || '',
          taxOffice: invoiceData.accountTaxOffice || '',
          address: invoiceData.accountAddress || '',
        },
        items: invoiceData.items?.map((item: any) => ({
          id: item.id,
          itemId: item.itemId,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit || 'adet',
          unitPrice: item.unitPrice,
          vatRate: item.vatRate,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          lineTotal: item.lineTotal,
          netTotal: item.netTotal,
          vatAmount: item.vatAmount,
          sctAmount: item.sctAmount || 0,
          grossTotal: item.grossTotal,
        })) || [],
        subtotal: invoiceData.subtotal,
        vatTotal: invoiceData.vatTotal,
        sctTotal: invoiceData.sctTotal || 0,
        discountTotal: invoiceData.discountTotal || 0,
        grandTotal: invoiceData.grandTotal,
        currency: invoiceData.currency || 'TRY',
        notes: invoiceData.notes || '',
        description: invoiceData.description || '',
      };

      setSelectedInvoiceData(formattedInvoiceData);
      setIsInvoiceDisplayModalOpen(true);
      setLoadingInvoice(false);

    } catch (error: any) {
      console.error('❌ [FATURA GÖRÜNTÜLE] Hata:', error);
      setErrorMessage(t('current.report.errors.invoiceLoadFailed', { error: handleApiError(error) }));
      setErrorSnackbarOpen(true);
      setLoadingInvoice(false);
    }
  };

  // ✅ YENİ: Faturayı düzenle (Sayfaya yönlendir)
  const handleEditInvoice = () => {
    if (!selectedTransaction?.referenceId) {
      setErrorMessage(t('current.report.errors.invoiceNotFound'));
      setErrorSnackbarOpen(true);
      handleCloseMenu();
      return;
    }

    handleCloseMenu();

    const transactionType = selectedTransaction.transactionType;
    const invoiceId = selectedTransaction.referenceId;

    console.log('✏️ [FATURA DÜZENLE] Yönlendiriliyor...', { 
      transactionType, 
      invoiceId,
      invoiceNumber: selectedTransaction.invoiceNumber 
    });

    // İşlem türüne göre doğru sayfaya yönlendir
    if (transactionType === 'SALES_INVOICE') {
      navigate(`/fatura/satis/duzenle/${invoiceId}`);
    } else if (transactionType === 'PURCHASE_INVOICE') {
      navigate(`/fatura/alis/duzenle/${invoiceId}`);
    } else {
      // Diğer durumlar için satış faturası sayfasına yönlendir
      navigate(`/fatura/satis/duzenle/${invoiceId}`);
    }
  };

  // Fatura görüntüleme modalını kapat
  const handleCloseInvoiceDisplayModal = () => {
    setIsInvoiceDisplayModalOpen(false);
    setSelectedInvoiceData(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmModalOpen(false);
    setTransactionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;

    try {
      setIsDeleteConfirmModalOpen(false);
      setLoading(true);
      setError(null);

      const numericTransactionId = parseInt(transactionToDelete.id, 10);
      if (isNaN(numericTransactionId)) {
        throw new Error(t('current.report.errors.invalidTransactionId'));
      }

      console.log('🗑️ İşlem siliniyor:', {
        id: numericTransactionId,
        type: transactionToDelete.transactionType,
        amount: transactionToDelete.amount
      });

      const deleteRequest = {
        companyId: companyId || '', // ✅ MULTI-TENANT: companyId eklendi
        reason: 'Kullanıcı tarafından manuel olarak silindi',
        deletedBy: ownAuthSession.getCurrentEmail() || 'Unknown'
      };

      const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_DELETE(numericTransactionId), deleteRequest);

      const result = response.data;
      console.log('✅ İşlem silindi:', result);

      setSuccessMessage(t('current.report.deleteSuccess', { logId: result.logId || 'N/A' }));
      setSuccessSnackbarOpen(true);

      // İşlemleri yeniden yükle
      if (companyId) {
        await loadAccountTransactions(companyId);
      }

      setLoading(false);
      setTransactionToDelete(null);
    } catch (error: any) {
      console.error('❌ İşlem silinirken hata:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Response status:', error.response?.status);
      setError(handleApiError(error));
      setLoading(false);
      setTransactionToDelete(null);
      
      setErrorMessage(t('current.report.errors.deleteFailed', { error: handleApiError(error) }));
      setErrorSnackbarOpen(true);
    }
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Cari Hareketler');
      
      // Başlık oluşturma
      let reportTitle = 'Cari Hareketler Raporu';
      if (startDate && endDate) {
        reportTitle += ` (${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')})`;
      } else if (startDate) {
        reportTitle += ` (${format(startDate, 'dd.MM.yyyy')} sonrası)`;
      } else if (endDate) {
        reportTitle += ` (${format(endDate, 'dd.MM.yyyy')} öncesi)`;
      }
      
      // Başlık satırı
      worksheet.mergeCells('A1:J1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = reportTitle;
      titleCell.font = { bold: true, size: 16 };
      titleCell.alignment = { horizontal: 'center' };
      worksheet.addRow([]);  // Boş satır ekle
      
      // Filtre bilgisi
      let filterInfo = [];
      if (selectedTransactionType) {
        filterInfo.push(`${t('current.report.filters')}: ${getTransactionTypeLabel(selectedTransactionType || undefined)}`);
      }
      if (accountSearchTerm) {
        filterInfo.push(`${t('current.report.columns.account')}: ${accountSearchTerm}`);
      }
      if (selectedSalesScreens.length > 0) {
        filterInfo.push(`${t('current.report.columns.screenName')}: ${selectedSalesScreens.length}`);
      }
      
      if (filterInfo.length > 0) {
        worksheet.mergeCells('A2:J2');
        const filterCell = worksheet.getCell('A2');
        filterCell.value = `Filtreler: ${filterInfo.join(', ')}`;
        filterCell.font = { italic: true };
        worksheet.addRow([]);  // Boş satır ekle
      }
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: 'Tarih', key: 'date', width: 20 },
        { header: t('current.report.columns.transactionType'), key: 'transactionType', width: 20 },
        { header: t('current.report.columns.screenName'), key: 'screenName', width: 20 },
        { header: 'Cari Hesap', key: 'account', width: 30 },
        { header: t('current.report.description'), key: 'description', width: 40 },
        { header: 'Belge No', key: 'documentNo', width: 15 },
        { header: t('current.report.columns.debitAmount'), key: 'debitAmount', width: 15 },
        { header: 'Alacak', key: 'creditAmount', width: 15 },
        { header: 'Bakiye', key: 'balance', width: 15 },
        { header: 'Para Birimi', key: 'currency', width: 10 },
      ];
      
      // Başlık satırı için satır indeksini belirle (filtre satırları eklendiği için dinamik olarak hesapla)
      const headerRowIndex = filterInfo.length > 0 ? 4 : 3;
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(headerRowIndex).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // İşlemleri tarih sırasına göre sırala (eskiden yeniye)
      const sortedTransactions = [...filteredTransactions].sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      
      // Cari işlemleri Excel'e aktar
      sortedTransactions.forEach(transaction => {
        const transactionType = transaction.transactionType || transaction.type;
        let typeName = getTransactionTypeLabel(transactionType);
        
        // Fatura işlemi türleri
        if (transactionType === TRANSACTION_TYPES.INVOICE) {
          if (transaction.operationType === 'sales') {
            typeName = 'Satış Faturası';
          } else if (transaction.operationType === 'purchase') {
            typeName = 'Alış Faturası';
          }
        }
        
        worksheet.addRow({
          date: transaction.date ? formatDate(transaction.date, transaction) : formatDate(transaction.createdAt, transaction),
          transactionType: typeName,
          screenName: transaction.screenName || '-',
          account: transaction.accountName || '-',
          description: transaction.description || '-',
          documentNo: transaction.documentNo || '-',
          debitAmount: transaction.debitAmount ? Number(transaction.debitAmount) : 0,
          creditAmount: transaction.creditAmount ? Number(transaction.creditAmount) : 0,
          balance: transaction.nextBalance,
          currency: transaction.currency || 'TRY'
        });
      });
      
      // Toplam satırını ekle
      const lastRowIndex = worksheet.rowCount + 1;
      const totalRow = worksheet.addRow({
        date: t('current.table.grandTotal'),
        debitAmount: calculateTotals.totalDebit,
        creditAmount: calculateTotals.totalCredit,
        balance: calculateTotals.totalCredit - calculateTotals.totalDebit,
      });
      
      // Toplam satırı stilini ayarla
      totalRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF2F2F2' } };
      });
      
      // Para birimi formatlaması
      worksheet.getColumn('debitAmount').numFmt = '#,##0.00 ₺';
      worksheet.getColumn('creditAmount').numFmt = '#,##0.00 ₺';
      worksheet.getColumn('balance').numFmt = '#,##0.00 ₺';
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > headerRowIndex && rowNumber !== lastRowIndex) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              ...cell.style,
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `Cari_Hareketler_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setError(null); // Varsa hata mesajını temizle
      console.log(`${filteredTransactions.length} adet cari hareket Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(t('current.report.errors.excelExport'));
    } finally {
      setLoadingExport(false);
    }
  };

  // Toplam tutar hesaplama
  const calculateTotals = useMemo(() => {
    let totalAmount = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    
    filteredTransactions.forEach(transaction => {
      totalAmount += transaction.amount || 0;
      totalDebit += transaction.debitAmount || 0;
      totalCredit += transaction.creditAmount || 0;
    });
    
    return {
      totalAmount,
      totalDebit,
      totalCredit
    };
  }, [filteredTransactions]);

  // İşlem türüne göre renk belirleme
  const getTransactionColor = (transaction: Transaction) => {
    // Hızlı satış için mavi renk kullanacağız
    if (transaction.transactionType === TRANSACTION_TYPES.QUICK_SALE || 
        (transaction.screenId && salesScreens.some(screen => screen.id === transaction.screenId && screen.type === 'quicksale'))) {
      return 'rgba(25, 118, 210, 0.04)'; // Mavi tonunda bir arkaplan
    }
    
    // Diğer işlemler için gelir/gider durumuna göre renk belirle
    return transaction.isIncome 
      ? 'rgba(76, 175, 80, 0.04)'  // Gelir için yeşil ton
      : 'rgba(244, 67, 54, 0.04)'; // Gider için kırmızı ton
  };

  // ─── Kolon Tanımları (Dinamik — visible columns'a göre) ───
  const reportColumns = useMemo<DataTableColumn<Transaction>[]>(() => {
    return visibleColumns.map(column => {
      switch (column.id) {
        case 'date':
          return {
            id: 'date',
            label: t('current.report.columns.date'),
            field: 'date' as keyof Transaction,
            sortable: true,
            render: (t: Transaction) => t.date ? formatDate(t.date, t) : formatDate(t.createdAt, t),
          };
        case 'transactionType':
          return {
            id: 'transactionType',
            label: t('current.report.columns.transactionType'),
            field: 'transactionType' as keyof Transaction,
            sortable: true,
            render: (t: Transaction) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {TRANSACTION_TYPE_ICONS[t.transactionType || t.type] || (t.isIncome ? <ArrowDownwardIcon color="success" /> : <ArrowUpwardIcon color="error" />)}
                <Chip
                  label={getTransactionTypeLabel(t.transactionType || t.type)}
                  size="small"
                  color={getTransactionTypeChipColor(t.transactionType || t.type)}
                  variant="outlined"
                />
              </Box>
            ),
          };
        case 'screenName':
          return {
            id: 'screenName',
            label: t('current.report.columns.screenName'),
            render: (t: Transaction) => t.screenName || '-',
          };
        case 'account':
          return {
            id: 'account',
            label: t('current.report.columns.account'),
            field: 'accountName' as keyof Transaction,
            sortable: true,
            render: (t: Transaction) => (
              <Tooltip title={`Cari ID: ${t.accountId || 'Bilinmiyor'}`}>
                <Typography variant="body2" fontWeight="medium">{t.accountName || '-'}</Typography>
              </Tooltip>
            ),
          };
        case 'previousBalance':
          return {
            id: 'previousBalance',
            label: t('current.report.columns.previousBalance'),
            field: 'previousBalance' as keyof Transaction,
            sortable: true,
            align: 'right' as const,
            render: (t: Transaction) => formatCurrency(t.previousBalance, t.currency),
          };
        case 'amount':
          return {
            id: 'amount',
            label: t('current.report.columns.amount'),
            field: 'amount' as keyof Transaction,
            sortable: true,
            align: 'right' as const,
            render: (t: Transaction) => (
              <Box sx={{ color: t.isIncome ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                {formatCurrency(t.amount, t.currency)}
              </Box>
            ),
          };
        case 'debitAmount':
          return {
            id: 'debitAmount',
            label: t('current.report.columns.debitAmount'),
            field: 'debitAmount' as keyof Transaction,
            sortable: true,
            align: 'right' as const,
            render: (t: Transaction) => t.debitAmount ? formatCurrency(t.debitAmount, t.currency) : '-',
          };
        case 'creditAmount':
          return {
            id: 'creditAmount',
            label: t('current.report.columns.creditAmount'),
            field: 'creditAmount' as keyof Transaction,
            sortable: true,
            align: 'right' as const,
            render: (t: Transaction) => t.creditAmount ? formatCurrency(t.creditAmount, t.currency) : '-',
          };
        case 'nextBalance':
          return {
            id: 'nextBalance',
            label: t('current.report.columns.nextBalance'),
            field: 'nextBalance' as keyof Transaction,
            sortable: true,
            align: 'right' as const,
            render: (t: Transaction) => formatCurrency(t.nextBalance, t.currency),
          };
        case 'currency':
          return {
            id: 'currency',
            label: t('current.report.columns.currency'),
            render: (t: Transaction) => t.currency || 'TRY',
          };
        case 'actions':
          return {
            id: 'actions',
            label: t('current.report.columns.actions'),
            align: 'center' as const,
            width: '60px',
            render: (t: Transaction) => (
              <IconButton
                size="small"
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  event.stopPropagation();
                  handleOpenMenu(event, t);
                }}
                sx={{ color: 'text.secondary' }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          };
        default:
          return {
            id: column.id,
            label: column.label,
            render: () => '-',
          };
      }
    });
  }, [visibleColumns, t]);

  return (
    <ScrollableContent>
      {/* Üst Başlık ve Butonlar */}
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <IconButton
            onClick={handleGoBack}
            color="primary"
            sx={{ mr: 1, display: { xs: 'flex', md: 'none' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
          >
            {t('current.report.back')}
          </Button>
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('current.report.title')}
          </Typography>
        </Box>
        {/* Desktop Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <IconButton
            color="primary"
            onClick={handleOpenTopActionsMenu}
            aria-label={t('current.report.columns.actions')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Mobil Butonlar */}
        <Grid container spacing={1} sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? "primary" : "inherit"}
            >
              {showFilters ? t('current.report.hideFilters') : t('current.report.filters')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="info"
              startIcon={<HistoryIcon />}
              onClick={handleOpenLogsModal}
            >
              {t('current.report.logs')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
              disabled={loadingExport || loading || filteredTransactions.length === 0}
            >
              {loadingExport ? t('current.menu.preparing') : t('current.report.excelShort')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenTableSettingsModal}
            >
              {t('current.report.customizeShort')}
            </Button>
          </Grid>
        </Grid>
      </HeaderContainer>

      {/* Arama ve Filtreleme */}
      {/* Desktop'ta her zaman göster, mobilde showFilters ile kontrol et */}
      <Box sx={{ display: { xs: showFilters ? 'block' : 'none', md: 'block' } }}>
        <FilterContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 1 } }}>
          <TextField
            size="small"
            placeholder={t('current.report.searchTransaction')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', md: 200 } }}
          />
          
          <TextField
            size="small"
            placeholder={t('current.report.searchByAccount')}
            value={accountSearchTerm}
            onChange={(e) => setAccountSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', md: 200 } }}
          />
          
          <Autocomplete
            size="small"
            options={Object.keys(TRANSACTION_TYPE_NAMES)}
            getOptionLabel={(option) => getTransactionTypeLabel(option)}
            value={selectedTransactionType}
            onChange={(_, newValue) => setSelectedTransactionType(newValue)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder={t('current.report.searchByTransactionType')}
                size="small"
              />
            )}
            sx={{ width: { xs: '100%', md: 200 } }}
          />
          
          {/* Tarih Aralığı Filtreleri */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'en' ? enLocale : trLocale}>
            <DatePicker
              label={t('current.report.startDate')}
              value={startDate}
              format="dd.MM.yyyy"
              onChange={(newDate) => setStartDate(newDate)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: '100%', md: 150 } },
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }
                }
              }}
            />
            
            <DatePicker
              label={t('current.report.endDate')}
              value={endDate}
              format="dd.MM.yyyy"
              onChange={(newDate) => setEndDate(newDate)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: '100%', md: 150 } },
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }
                }
              }}
            />
          </LocalizationProvider>
          
          {/* Satış Ekranı Filtresi */}
          <Autocomplete
            size="small"
            multiple
            options={salesScreens}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedSalesScreens.map(id => salesScreens.find(screen => screen.id === id) || { id, name: 'Bilinmeyen Ekran', type: 'unknown' })}
            onChange={(_, newValue) => setSelectedSalesScreens(newValue.map(item => item.id))}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder={t('current.report.searchBySalesScreen')}
                size="small"
              />
            )}
            sx={{ width: { xs: '100%', md: 200 } }}
          />
          
          {/* Şube Filtresi */}
          <TextField
            select
            size="small"
            label={t('current.filters.branchFilter')}
            value={selectedBranchFilter}
            disabled={!!lockedBranchId}
            onChange={(e) => {
              setSelectedBranchFilter(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: '100%', md: 180 } }}
          >
            <MenuItem value="">{t('current.filters.allBranches')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
        </FilterContainer>
      </Box>

      {/* Aktif filtre bilgisi */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {accountSearchTerm && (
          <Chip
            label={`${t('current.report.columns.account')}: ${accountSearchTerm}`}
            onDelete={handleClearAccountFilter}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {selectedTransactionType && (
          <Chip
            label={`${t('current.report.filters')}: ${getTransactionTypeLabel(selectedTransactionType || undefined)}`}
            onDelete={handleClearTransactionTypeFilter}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {selectedSalesScreens.length > 0 && (
          <Chip
            label={`${t('current.report.columns.screenName')}: ${selectedSalesScreens.length}`}
            onDelete={handleClearSalesScreenFilter}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {selectedBranchFilter && (
          <Chip
            label={`${t('current.filters.branchFilter')}: ${branches.find(b => b.id === selectedBranchFilter)?.name || selectedBranchFilter}`}
            onDelete={lockedBranchId ? undefined : () => { setSelectedBranchFilter(''); setPage(0); }}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {(startDate || endDate) && (
          <Chip
            icon={<DateRangeIcon />}
            label={`Tarih: ${startDate ? format(startDate, 'dd.MM.yyyy') : '...'} - ${endDate ? format(endDate, 'dd.MM.yyyy') : '...'}`}
            onDelete={handleClearDateFilters}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {(accountSearchTerm || selectedTransactionType || selectedSalesScreens.length > 0 || startDate || endDate) && (
          <Chip
            label={t('current.report.clearAllFilters')}
            onDelete={handleClearAllFilters}
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </Box>

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          // Mobilde göster, desktop'ta AccountingDataTable kendi loading'ini gösterir
          display2: { xs: 'flex', md: 'none' }
        }}>
          <CircularProgress />
        </Box>
      ) : null}

      {/* Desktop: AccountingDataTable */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <AccountingDataTable<Transaction>
          columns={reportColumns}
          rows={paginatedTransactions}
          getRowId={(t) => t.id}
          loading={loading}
          pagination={false}
          getRowSx={(t) => ({ backgroundColor: getTransactionColor(t) })}
          emptyMessage={searchTerm || accountSearchTerm || selectedTransactionType 
            ? t('current.emptyState.searchNotFound')
            : t('current.report.noTransactions')}
          emptyIcon={<PeopleIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
          ariaLabel="cari hareket raporu tablosu"
          onSort={handleSort}
          sortField={sortColumn}
          sortDirection={sortDirection}
          columnResizeStorageKey="account-movements-report"
        />
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage={t('dataTable.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          sx={DATATABLE_STYLES.pagination}
        />
        {/* GENEL TOPLAM */}
        {!loading && paginatedTransactions.length > 0 && (
          <Paper elevation={1} sx={{ mt: 1, p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight="bold">{t('current.table.grandTotal')}</Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography variant="subtitle2" color="error.main">
                {t('current.report.columns.debitAmount')}: {formatCurrency(calculateTotals.totalDebit, 'TRY')}
              </Typography>
              <Typography variant="subtitle2" color="success.main">
                {t('current.report.columns.creditAmount')}: {formatCurrency(calculateTotals.totalCredit, 'TRY')}
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('current.report.columns.amount')}: {formatCurrency(calculateTotals.totalAmount, 'TRY')}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Mobile: Kart Görünümü */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {!loading && filteredTransactions.length === 0 ? (
          <Paper elevation={3} sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)'
          }}>
            <PeopleIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              Cari Hareket Listesi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchTerm || accountSearchTerm || selectedTransactionType 
                ? t('current.emptyState.searchNotFound')
                : t('current.report.noTransactions')
              }
            </Typography>
          </Paper>
        ) : !loading && (
          <>
            <Grid container spacing={2} sx={{ p: 2 }}>
              {paginatedTransactions.map((transaction) => (
                <Grid item xs={12} key={transaction.id}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: transaction.isIncome ? 'success.main' : 'error.main',
                      bgcolor: transaction.isIncome ? 'success.50' : 'error.50',
                      '&:hover': {
                        boxShadow: 4,
                        borderColor: transaction.isIncome ? 'success.dark' : 'error.dark'
                      }
                    }}
                  >
                    {/* Başlık: Cari Hesap ve İşlem Menüsü */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PeopleIcon sx={{ fontSize: 20, color: transaction.isIncome ? 'success.main' : 'error.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary" noWrap>
                          {transaction.accountName || '-'}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={(event) => handleOpenMenu(event, transaction)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    {/* İşlem Tipi */}
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">{t('current.report.columns.transactionType')}:</Typography>
                      <Chip
                        label={getTransactionTypeLabel(transaction.transactionType || transaction.type)}
                        size="small"
                        color={getTransactionTypeChipColor(transaction.transactionType || transaction.type)}
                        sx={{ height: 18, fontSize: '0.6rem' }}
                      />
                    </Box>

                    {/* Tarih */}
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">Tarih:</Typography>
                      <Typography variant="caption" fontWeight="medium">
                        {transaction.date ? formatDate(transaction.date, transaction) : formatDate(transaction.createdAt, transaction)}
                      </Typography>
                    </Box>

                    {/* Satış Ekranı (varsa) */}
                    {transaction.screenName && (
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('current.report.columns.screenName')}:</Typography>
                        <Typography variant="caption" fontWeight="medium" noWrap>{transaction.screenName}</Typography>
                      </Box>
                    )}

                    {/* Açıklama */}
                    {transaction.description && (
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('current.report.description')}:</Typography>
                        <Typography variant="caption" fontWeight="medium" noWrap>{transaction.description}</Typography>
                      </Box>
                    )}

                    {/* Borç/Alacak */}
                    {(transaction.debitAmount || 0) > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('current.report.columns.debitAmount')}:</Typography>
                        <Typography variant="caption" fontWeight="medium" color="error.main">
                          {formatCurrency(transaction.debitAmount || 0, transaction.currency)}
                        </Typography>
                      </Box>
                    )}
                    {(transaction.creditAmount || 0) > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">Alacak:</Typography>
                        <Typography variant="caption" fontWeight="medium" color="success.main">
                          {formatCurrency(transaction.creditAmount || 0, transaction.currency)}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* Tutar */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">Tutar:</Typography>
                      <Typography variant="body2" fontWeight="bold" color={transaction.isIncome ? 'success.main' : 'error.main'}>
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Mobil Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              sx={DATATABLE_STYLES.pagination}
            />

            {/* Mobil Özet Kartı */}
            <Paper elevation={3} sx={{ p: 2, mt: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {t('current.table.grandTotal')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('current.report.totalDebit')}:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {formatCurrency(calculateTotals.totalDebit, 'TRY')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('current.report.totalCredit')}:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {formatCurrency(calculateTotals.totalCredit, 'TRY')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Box>
      
      {/* Sayfa altına ek boşluk */}
      <Box sx={{ height: '300px' }} />

      <Menu
        anchorEl={topActionsAnchorEl}
        open={Boolean(topActionsAnchorEl)}
        onClose={handleCloseTopActionsMenu}
        PaperProps={{ elevation: 3, sx: { minWidth: 230 } }}
      >
        <MenuItem onClick={handleOpenReportInfoModal}>
          <ListItemIcon>
            <InfoIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.information')} />
        </MenuItem>
        <MenuItem onClick={() => { handleOpenLogsModal(); handleCloseTopActionsMenu(); }}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('current.report.logs')} />
        </MenuItem>
        <MenuItem
          onClick={() => { exportToExcel(); handleCloseTopActionsMenu(); }}
          disabled={loadingExport || loading || filteredTransactions.length === 0}
        >
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={loadingExport ? t('current.report.preparingExcel') : t('current.report.downloadExcel')} />
        </MenuItem>
        <MenuItem onClick={() => { handleOpenTableSettingsModal(); handleCloseTopActionsMenu(); }}>
          <ListItemIcon>
            <ViewColumnIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('current.menu.customizeTable')} />
        </MenuItem>
      </Menu>

      <Dialog
        open={isReportInfoModalOpen}
        onClose={handleCloseReportInfoModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            <Typography variant="h6">{t('current.report.infoModal.title')}</Typography>
          </Box>
          <IconButton onClick={handleCloseReportInfoModal} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              {t('current.report.infoModal.overviewTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('current.report.infoModal.overviewText')}
            </Typography>
          </Alert>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {t('current.report.infoModal.filtersTitle')}
            </Typography>
            <Typography variant="body2">• {t('current.report.infoModal.filtersItem1')}</Typography>
            <Typography variant="body2">• {t('current.report.infoModal.filtersItem2')}</Typography>
            <Typography variant="body2">• {t('current.report.infoModal.filtersItem3')}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {t('current.report.infoModal.columnsTitle')}
            </Typography>
            <Typography variant="body2">• {t('current.report.infoModal.columnsItem1')}</Typography>
            <Typography variant="body2">• {t('current.report.infoModal.columnsItem2')}</Typography>
            <Typography variant="body2">• {t('current.report.infoModal.columnsItem3')}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {t('current.report.infoModal.operationsTitle')}
            </Typography>
            <Typography variant="body2">• {t('current.report.infoModal.operationsItem1')}</Typography>
            <Typography variant="body2">• {t('current.report.infoModal.operationsItem2')}</Typography>
            <Typography variant="body2">• {t('current.report.infoModal.operationsItem3')}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportInfoModal} variant="contained">{t('current.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Tablo Ayarları Modalı */}
      <Dialog 
        open={isTableSettingsModalOpen} 
        onClose={handleCloseTableSettingsModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ 
          sx: { 
            borderRadius: 2,
          } 
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            fontWeight: 'bold',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>{t('current.modals.tableSettings.title')}</Typography>
          <IconButton color="inherit" onClick={() => setShowTableSettingsInfoBox(prev => !prev)} aria-label={t('current.menu.information')}>
            <InfoIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          {showTableSettingsInfoBox && (
            <Alert severity="info" sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {t('current.report.tableSettingsInfo.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('current.report.tableSettingsInfo.description')}
              </Typography>
              <Typography variant="body2">{t('current.report.tableSettingsInfo.item1')}</Typography>
              <Typography variant="body2">{t('current.report.tableSettingsInfo.item2')}</Typography>
              <Typography variant="body2">{t('current.report.tableSettingsInfo.item3')}</Typography>
            </Alert>
          )}
          
          {/* Sabit Sütunlar */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
              <LockIcon sx={{ mr: 1 }} /> {t('current.modals.tableSettings.fixedColumns')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('current.modals.tableSettings.fixedColumnsInfo')}
            </Typography>
            
            <Grid container spacing={2}>
              {tableColumns.filter(column => column.fixed).map((column) => (
                <Grid item xs={12} sm={6} md={4} key={column.id}>
                  <Box 
                    sx={{ 
                      p: 1.5, 
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DragIndicatorIcon sx={{ color: 'primary.contrastText', mr: 1, opacity: 0.7 }} />
                      <Typography variant="body1">{column.label}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={true}
                            disabled
                            size="small"
                          />
                        }
                        label=""
                      />
                      
                      {column.description && (
                        <Tooltip title={column.description}>
                          <IconButton size="small" sx={{ color: 'primary.contrastText' }}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
          
          {/* Özelleştirilebilir Sütunlar */}
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
              <DragIndicatorIcon sx={{ mr: 1 }} /> {t('current.modals.tableSettings.customizableColumns')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('current.modals.tableSettings.customizableColumnsInfo')}
            </Typography>
            
            <Grid container spacing={2}>
              {tableColumns.filter(column => !column.fixed).map((column) => (
                <Grid item xs={12} sm={6} md={4} key={column.id}>
                  <Box 
                    sx={{ 
                      p: 1.5, 
                      border: '1px solid',
                      borderColor: column.visible ? 'primary.light' : 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: column.visible ? 'rgba(0, 0, 0, 0.04)' : 'background.paper'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1, opacity: 0.7 }} />
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: column.visible ? 'text.primary' : 'text.secondary',
                          fontWeight: column.visible ? 'medium' : 'normal'
                        }}
                      >
                        {column.label}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={column.visible}
                            onChange={() => {
                              setTableColumns(prevColumns =>
                                prevColumns.map(prevColumn => {
                                  if (prevColumn.id === column.id && !prevColumn.fixed) {
                                    return { ...prevColumn, visible: !prevColumn.visible };
                                  }
                                  return prevColumn;
                                })
                              );
                            }}
                            size="small"
                          />
                        }
                        label=""
                      />
                      
                      {column.description && (
                        <Tooltip title={column.description}>
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseTableSettingsModal}
            variant="outlined"
          >
            {t('current.actions.cancel')}
          </Button>
          <Button 
            onClick={() => {
              saveTableSettings(tableColumns);
              handleCloseTableSettingsModal();
            }}
            variant="contained"
            color="primary"
          >
            {t('current.modals.tableSettings.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İşlem Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: '200px' }
        }}
      >
        {/* ✅ YENİ: Fatura işlemleri için Görüntüle ve Kaydı Aç butonları */}
        {(selectedTransaction?.transactionType === 'SALES_INVOICE' || 
          selectedTransaction?.transactionType === 'PURCHASE_INVOICE') && 
          selectedTransaction?.referenceId && (
          <>
            <MenuItem onClick={handleViewInvoice} disabled={loadingInvoice}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={loadingInvoice ? t('current.report.loading') : t('current.report.view')} />
            </MenuItem>
            {!lockedBranchId && (
            <MenuItem onClick={handleEditInvoice}>
              <ListItemIcon>
                <EditIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText primary={t('current.report.openRecord')} />
            </MenuItem>
            )}
            <Divider />
          </>
        )}
        <MenuItem onClick={handleDeleteTransaction}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('current.report.delete')} sx={{ color: 'error.main' }} />
        </MenuItem>
      </Menu>

      {/* Silme Onay Modalı */}
      <Dialog
        open={isDeleteConfirmModalOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          // ✅ Çek/Senet hareketi kontrolü - çeke bağlı hareketler buradan silinemez,
          // Çek/Senet Yönetimi'nden çek silinince tüm hareketler birlikte silinir.
          const isCheckNoteTransaction =
            (transactionToDelete?.referenceType || '').startsWith('CHECK_NOTE') ||
            ['CHECK_RECEIVED', 'NOTE_RECEIVED', 'CHECK_GIVEN', 'NOTE_GIVEN',
             'CHECK_RETURNED', 'NOTE_RETURNED', 'CHECK_BOUNCED', 'NOTE_BOUNCED',
             'CHECK_ENDORSED', 'NOTE_ENDORSED'].includes(transactionToDelete?.transactionType || '');

          // Entegrasyon işlemi kontrolü - transactionType veya açıklamada fatura numarası varsa
          const isIntegrationTransaction = !isCheckNoteTransaction && (
            transactionToDelete?.transactionType === 'QUICK_SALE' || 
            transactionToDelete?.transactionType === 'SALES_INVOICE' ||
            transactionToDelete?.transactionType === 'PURCHASE_INVOICE' ||
            // Faturaya bağlı tahsilat hareketleri (hızlı satıştan gelen)
            (transactionToDelete?.transactionType === 'COLLECTION' && 
             (transactionToDelete?.referenceId || /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(transactionToDelete?.description || ''))) ||
            // Açıklamada fatura numarası pattern'i varsa (SF-XXXXXX veya AF-XXXXXX)
            /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(transactionToDelete?.description || ''));
          
          // Fatura numarasını açıklamadan çıkar veya invoiceNumber alanından al
          const invoiceNumberMatch = transactionToDelete?.description?.match(/[SA]F-\d{6}-\d{4}/);
          const extractedInvoiceNumber = transactionToDelete?.invoiceNumber || (invoiceNumberMatch ? invoiceNumberMatch[0] : null);

          return (
            <>
              <DialogTitle
                sx={{
                  bgcolor: (isIntegrationTransaction || isCheckNoteTransaction) ? 'warning.main' : 'error.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <WarningIcon />
                <Typography variant="h6">
                  {isCheckNoteTransaction
                    ? t('current.report.checkNoteTransaction')
                    : isIntegrationTransaction
                      ? t('current.report.integrationTransaction')
                      : t('current.report.deleteConfirmationTitle')}
                </Typography>
              </DialogTitle>

              <DialogContent sx={{ mt: 2 }}>
                {/* Çek/Senet hareketleri için özel uyarı */}
                {isCheckNoteTransaction && (
                  <>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {t('current.report.checkNoteDeleteBlocked')}
                      </Typography>
                    </Alert>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {t('current.report.checkNoteDeleteInfo')}
                      </Typography>
                      {transactionToDelete?.invoiceNumber && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          {t('current.report.checkNoteDocumentNumber')}: {transactionToDelete.invoiceNumber}
                        </Typography>
                      )}
                    </Alert>

                    {transactionToDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.transactionType')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {getTransactionTypeLabel(transactionToDelete.transactionType || transactionToDelete.type)}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.amount')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              {formatCurrency(transactionToDelete.amount, transactionToDelete.currency || 'TRY')}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.description')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.description || '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </>
                )}

                {/* Entegrasyon işlemleri için özel uyarı */}
                {!isCheckNoteTransaction && (isIntegrationTransaction ? (
                  <>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {t('current.report.integrationDeleteBlocked')}
                      </Typography>
                    </Alert>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {t('current.report.integrationDeleteInfo')}
                      </Typography>
                      {extractedInvoiceNumber && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          {t('current.report.invoiceNumber')}: {extractedInvoiceNumber}
                        </Typography>
                      )}
                    </Alert>

                    {transactionToDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.transactionType')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {getTransactionTypeLabel(transactionToDelete.transactionType || transactionToDelete.type)}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.amount')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              {formatCurrency(transactionToDelete.amount, transactionToDelete.currency || 'TRY')}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.description')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.description || '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <DialogContentText sx={{ mb: 2 }}>
                      {t('current.report.deleteConfirmationText')}
                    </DialogContentText>

                    {transactionToDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.transactionType')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {getTransactionTypeLabel(transactionToDelete.transactionType || transactionToDelete.type)}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.amount')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              {formatCurrency(transactionToDelete.amount, transactionToDelete.currency || 'TRY')}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.description')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.description || '-'}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('current.report.columns.date')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.date 
                                ? formatDate(transactionToDelete.date, transactionToDelete) 
                                : formatDate(transactionToDelete.createdAt, transactionToDelete)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {t('current.report.deleteWarning')}
                      </Typography>
                    </Alert>
                  </>
                ))}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                  onClick={handleCancelDelete} 
                  variant="outlined"
                  color="inherit"
                >
                  {(isIntegrationTransaction || isCheckNoteTransaction) ? t('current.actions.close') : t('current.actions.cancel')}
                </Button>
                
                {/* Çek/Senet hareketleri için Çek/Senet Yönetimi butonu */}
                {isCheckNoteTransaction ? (
                  <Button
                    onClick={() => {
                      handleCancelDelete();
                      navigate('/cek-senet');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<DescriptionIcon />}
                  >
                    {t('current.report.goToCheckNoteManagement')}
                  </Button>
                ) : isIntegrationTransaction ? (
                  <Button 
                    onClick={() => {
                      handleCancelDelete();
                      navigate('/fatura/satis');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<DescriptionIcon />}
                  >
                    {t('current.report.goToInvoiceManagement')}
                  </Button>
                ) : (
                  <Button 
                    onClick={handleConfirmDelete} 
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    autoFocus
                  >
                    {t('current.report.delete')}
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Başarı Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccessSnackbarOpen(false)} 
          severity="success" 
          variant="filled"
          icon={<CheckCircleIcon />}
          sx={{ 
            width: '100%',
            fontSize: '1rem'
          }}
        >
          <Typography variant="body1" fontWeight="medium">
            {successMessage}
          </Typography>
        </Alert>
      </Snackbar>

      {/* Hata Snackbar */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={8000}
        onClose={() => setErrorSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setErrorSnackbarOpen(false)} 
          severity="error" 
          variant="filled"
          icon={<ErrorIcon />}
          sx={{ 
            width: '100%',
            fontSize: '1rem'
          }}
        >
          <Typography variant="body1" fontWeight="medium">
            {errorMessage}
          </Typography>
        </Alert>
      </Snackbar>

      {/* Loglar Modalı */}
      <TransactionLogsModal
        open={isLogsModalOpen}
        onClose={handleCloseLogsModal}
        companyId={companyId}
      />

      {/* ✅ YENİ: Fatura Görüntüleme Modalı */}
      {selectedInvoiceData && (
        <InvoiceDisplayModal
          open={isInvoiceDisplayModalOpen}
          onClose={handleCloseInvoiceDisplayModal}
          invoice={selectedInvoiceData}
          companyId={companyId}
        />
      )}
    </ScrollableContent>
  );
};

export default AccountMovementsReport; 